import React, {useEffect, useRef} from 'react';
import {connect} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {makeStyles} from "@material-ui/core";
import cx from "classnames";
import Preloader from "../Preloader/Preloader";
import EmptyState from "../Shared/EmptyState/EmptyState";
import BlueButton from "../Shared/BlueButton/BlueButton";
import {openSlateInPane} from "../../store/actions/slatePane";
import {API_ROOT_URI} from "../../config/constants";
import {SET_GLOBAL_DATA, VISIBILITY_CHANGE, PANE_STATE_CHANGE} from "../../config/iframeMessageTypes";

var IFRAME_LOAD_TIMEOUT = 5000;
var LOADING_INDICATOR_DELAY = 1000;

var useStyles = makeStyles(function() {
  return {
    container: {
      position: 'absolute',
      top: 32,
      left: 30,
      right: 30,
      bottom: 23,
      pointerEvents: 'none',
      display: 'none',
      '& .slate-pane-bar': {
        visibility: 'visible',
      },
      '& .slate-pane-next': {
        display: 'none',
      },
    },
    containerActive: {
      pointerEvents: 'auto',
      display: 'block',
    },
    moduleWrapper: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'none',
      zIndex: 1,
    },
    moduleWrapperActive: {
      display: 'flex',
      '& .empty-state': {
        flex: 1,
        width: '100%',
        height: '100%',
      },
    },
    iframe: {
      width: '100%',
      height: '100%',
      border: 'none',
    },
    iframeLoading: {
      visibility: 'hidden',
      position: 'absolute',
    },
    errorContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '100%',
      padding: '32px',
      textAlign: 'center',
    },
    errorIcon: {
      fontSize: '64px',
      color: '#dc2626',
      marginBottom: '16px',
    },
    errorHeading: {
      fontSize: '20px',
      fontWeight: 600,
      color: '#374151',
      margin: '0 0 8px 0',
    },
    errorDescription: {
      fontSize: '14px',
      color: '#6b7280',
      margin: '0 0 24px 0',
      maxWidth: '400px',
    },
  };
});

function getPaneOffset() {
  var pagesEl = document.getElementById('pages');
  if (!pagesEl) return 0;
  var style = getComputedStyle(pagesEl);
  var docked = parseInt(style.getPropertyValue('--slate-pane-docked'), 10) || 0;
  var overlay = parseInt(style.getPropertyValue('--slate-pane-overlay'), 10) || 0;
  var collapsed = parseInt(style.getPropertyValue('--slate-pane-collapsed'), 10) || 0;
  return docked + overlay + collapsed;
}

// Individual iframe component with its own loading/error state
function ModuleIframe(props) {
  var slug = props.slug;
  var moduleKey = props.moduleKey;
  var contentUrl = props.contentUrl;
  var isActive = props.isActive;
  var currentAccount = props.currentAccount;
  var onOpenSlate = props.onOpenSlate;

  var translation = useTranslation();
  var t = translation.t;
  var classes = useStyles();

  var isIframeLoadingState = React.useState(true);
  var isIframeLoading = isIframeLoadingState[0];
  var setIsIframeLoading = isIframeLoadingState[1];

  var hasLoadErrorState = React.useState(false);
  var hasLoadError = hasLoadErrorState[0];
  var setHasLoadError = hasLoadErrorState[1];

  var iframeKeyState = React.useState(0);
  var iframeKey = iframeKeyState[0];
  var setIframeKey = iframeKeyState[1];

  var showLoadingIndicatorState = React.useState(false);
  var showLoadingIndicator = showLoadingIndicatorState[0];
  var setShowLoadingIndicator = showLoadingIndicatorState[1];

  var hasLoadedOnceRef = useRef(false);
  var previousAccountRef = useRef(null);

  var iframeRef = useRef(null);

  var sentNewMessageToIframe = function(type, message) {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      iframeRef.current.contentWindow.postMessage({
        type: type,
        message: message
      }, "*");
    }
  };

  // Listen for messages from this specific iframe
  useEffect(function() {
    var handler = function(ev) {
      if (!isActive) return;
      if (ev.data.type === 'openSlate') {
        onOpenSlate(ev.data.message);
      }
      if (ev.data.type === 'getPreviousSlateUuid' || ev.data.type === 'getNextSlateUuid') {
        // Forward to parent handler
      }
    };
    window.addEventListener('message', handler, false);
    return function() {
      window.removeEventListener('message', handler, false);
    };
  }, [isActive, onOpenSlate]);

  // Delayed loading indicator
  useEffect(function() {
    if (hasLoadError || !isIframeLoading) {
      return;
    }
    var delayId = setTimeout(function() {
      setShowLoadingIndicator(true);
    }, LOADING_INDICATOR_DELAY);
    return function() {
      clearTimeout(delayId);
    };
  }, [isIframeLoading, hasLoadError, iframeKey]);

  // Pre-check: verify URL is reachable before relying on iframe onLoad
  // (iframe onLoad fires even for browser error pages like ERR_CONNECTION_REFUSED)
  useEffect(function() {
    if (hasLoadError || !isIframeLoading) {
      return;
    }
    var cancelled = false;

    // Determine the actual iframe URL
    var checkUrl = contentUrl;
    var deployed = process.env.REACT_APP_ENV === 'production' || process.env.REACT_APP_ENV === 'development';
    if (!deployed) {
      checkUrl = 'http://localhost:3000';
    }
    var override = moduleKey ? process.env['REACT_APP_MODULE_URL_' + moduleKey] : undefined;
    if (override) {
      checkUrl = override;
    }

    fetch(checkUrl, { mode: 'no-cors', cache: 'no-store' })
      .then(function() {
        // URL is reachable -- let iframe load normally
      })
      .catch(function() {
        // Network error -- URL is unreachable
        if (!cancelled) {
          setHasLoadError(true);
          setIsIframeLoading(false);
          setShowLoadingIndicator(false);
        }
      });

    // Fallback timeout in case fetch hangs
    var timeoutId = setTimeout(function() {
      if (!cancelled && isIframeLoading && !hasLoadedOnceRef.current) {
        setHasLoadError(true);
        setIsIframeLoading(false);
      }
    }, IFRAME_LOAD_TIMEOUT);

    return function() {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [isIframeLoading, hasLoadError, iframeKey]);

  // Notify iframe of visibility changes
  useEffect(function() {
    if (!hasLoadedOnceRef.current) return;
    sentNewMessageToIframe(VISIBILITY_CHANGE, {
      isVisible: isActive,
    });
    if (isActive) {
      sentNewMessageToIframe(PANE_STATE_CHANGE, {
        rightPadding: getPaneOffset(),
      });
    }
  }, [isActive]);

  // Re-send global data when the account (stack) changes
  useEffect(function() {
    if (!hasLoadedOnceRef.current) {
      previousAccountRef.current = currentAccount;
      return;
    }
    if (previousAccountRef.current === null) {
      previousAccountRef.current = currentAccount;
      return;
    }
    previousAccountRef.current = currentAccount;
    sentNewMessageToIframe(SET_GLOBAL_DATA, {
      currentEnv: process.env.PUBLIC_URL || 'http://local.dev.slatepages.com:3001',
      currentStackUuid: currentAccount && currentAccount.uuid,
      rootApiUrl: API_ROOT_URI,
    });
  }, [currentAccount]);

  var handleIframeLoad = function() {
    hasLoadedOnceRef.current = true;
    setIsIframeLoading(false);
    setHasLoadError(false);
    setShowLoadingIndicator(false);

    // Send global data to iframe
    setTimeout(function() {
      sentNewMessageToIframe(SET_GLOBAL_DATA, {
        currentEnv: process.env.PUBLIC_URL || 'http://local.dev.slatepages.com:3001',
        currentStackUuid: currentAccount && currentAccount.uuid,
        rootApiUrl: API_ROOT_URI,
      });
      sentNewMessageToIframe(PANE_STATE_CHANGE, {
        rightPadding: getPaneOffset(),
      });
    }, 300);
    setTimeout(function() {
      sentNewMessageToIframe(SET_GLOBAL_DATA, {
        currentEnv: process.env.PUBLIC_URL || 'http://local.dev.slatepages.com:3001',
        currentStackUuid: currentAccount && currentAccount.uuid,
        rootApiUrl: API_ROOT_URI,
      });
      sentNewMessageToIframe(PANE_STATE_CHANGE, {
        rightPadding: getPaneOffset(),
      });
    }, 1000);
  };

  var handleIframeError = function() {
    setIsIframeLoading(false);
    setHasLoadError(true);
    setShowLoadingIndicator(false);
  };

  var handleRetry = function() {
    hasLoadedOnceRef.current = false;
    setHasLoadError(false);
    setIsIframeLoading(true);
    setShowLoadingIndicator(false);
    setIframeKey(function(prev) { return prev + 1; });
  };

  var isDeployedVersion = process.env.REACT_APP_ENV === 'production' || process.env.REACT_APP_ENV === 'development';
  var iframeSrc = isDeployedVersion ? contentUrl : 'http://localhost:3000';

  // Allow env var override: REACT_APP_MODULE_URL_<KEY> overrides the contentUrl
  var envOverride = moduleKey ? process.env['REACT_APP_MODULE_URL_' + moduleKey] : undefined;
  if (envOverride) {
    iframeSrc = envOverride;
  }

  var shouldShowPreloader = showLoadingIndicator && isIframeLoading && !hasLoadError;

  return React.createElement('div', {
    className: cx(classes.moduleWrapper, isActive && classes.moduleWrapperActive),
    'data-module-slug': slug,
  }, [
    shouldShowPreloader && React.createElement(Preloader, {key: 'preloader'}),
    hasLoadError && React.createElement(EmptyState, {
      key: 'error',
      icon: 'cloud_off',
      title: t('customModule:loadFailedTitle'),
      description: t('customModule:loadFailedDescription'),
      buttonLabel: t('customModule:retry'),
      buttonIcon: 'refresh',
      onAction: handleRetry,
    }),
    !hasLoadError && React.createElement('iframe', {
      key: 'iframe-' + iframeKey,
      ref: iframeRef,
      id: 'dynamic_content_' + slug,
      className: cx(classes.iframe, {
        [classes.iframeLoading]: isIframeLoading,
      }),
      src: iframeSrc,
      frameBorder: '0',
      sandbox: isDeployedVersion ? undefined : 'allow-scripts allow-same-origin',
      allowFullScreen: true,
      onLoad: handleIframeLoad,
      onError: handleIframeError,
    }),
  ]);
}

function CustomModuleContainer(props) {
  var activeCustomModuleSlug = props.activeCustomModuleSlug;
  var currentAccount = props.currentAccount;
  var currentUser = props.currentUser;
  var isPaneOpen = props.isPaneOpen;
  var isPaneDocked = props.isPaneDocked;
  var slateInSlatePaneUUID = props.slateInSlatePaneUUID;
  var openSlateInPaneProp = props.openSlateInPane;

  var classes = useStyles();

  // Track visited modules
  var visitedModulesState = React.useState([]);
  var visitedModules = visitedModulesState[0];
  var setVisitedModules = visitedModulesState[1];

  // Merge user and account modules
  var getMergedCustomModules = function() {
    var userModules = currentUser && currentUser.customModules ? currentUser.customModules : [];
    var accountModules = currentAccount && currentAccount.customModules ? currentAccount.customModules : [];

    var moduleMap = {};
    accountModules.forEach(function(module) {
      if (module.key) {
        moduleMap[module.key] = module;
      }
    });
    userModules.forEach(function(module) {
      if (module.key) {
        moduleMap[module.key] = module;
      }
    });

    return Object.keys(moduleMap).map(function(key) {
      return moduleMap[key];
    });
  };

  var mergedModules = getMergedCustomModules();

  var findModuleBySlug = function(slug) {
    for (var i = 0; i < mergedModules.length; i++) {
      if (mergedModules[i].slug === slug) {
        return mergedModules[i];
      }
    }
    return null;
  };

  // Add current module to visited list when it becomes active
  useEffect(function() {
    if (activeCustomModuleSlug && visitedModules.indexOf(activeCustomModuleSlug) === -1) {
      var module = findModuleBySlug(activeCustomModuleSlug);
      if (module) {
        setVisitedModules(function(prev) {
          return prev.concat([activeCustomModuleSlug]);
        });
      }
    }
  }, [activeCustomModuleSlug, mergedModules.length]);

  // Slate pane handler -- Redux update triggers the pane state useEffect
  var handleOpenSlate = function(slateUUID) {
    openSlateInPaneProp(slateUUID);
  };

  var sentNewMessageToActiveIframe = function(type, message) {
    if (!activeCustomModuleSlug) return;
    var iframe = document.getElementById('dynamic_content_' + activeCustomModuleSlug);
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage({
        type: type,
        message: message
      }, "*");
    }
  };

  // Notify active module iframe whenever pane state changes
  useEffect(function() {
    if (!activeCustomModuleSlug) return;
    sentNewMessageToActiveIframe(PANE_STATE_CHANGE, {
      rightPadding: getPaneOffset(),
    });
  }, [isPaneOpen, isPaneDocked, slateInSlatePaneUUID, activeCustomModuleSlug]);

  // Don't render anything if no modules have been visited
  if (visitedModules.length === 0) {
    return null;
  }

  var isActive = !!activeCustomModuleSlug;

  return React.createElement(React.Fragment, null,
    React.createElement('div', {
      className: cx(classes.container, isActive && classes.containerActive),
    },
      // Render all visited module iframes
      visitedModules.map(function(slug) {
        var module = findModuleBySlug(slug);
        if (!module) return null;
        return React.createElement(ModuleIframe, {
          key: slug,
          slug: slug,
          moduleKey: module.key,
          contentUrl: module.contentUrl,
          isActive: activeCustomModuleSlug === slug,
          currentAccount: currentAccount,
          onOpenSlate: handleOpenSlate,
        });
      })
    )
  );
}

var mapStateToProps = function(state) {
  return {
    activeCustomModuleSlug: state.slateViews.activeCustomModuleSlug,
    currentAccount: state.accounts.currentAccount,
    currentUser: state.currentUser,
    isPaneOpen: state.slatePane.isPaneOpen,
    isPaneDocked: state.slatePane.isPaneDocked,
    slateInSlatePaneUUID: state.slatePane.slateInSlatePaneUUID,
  };
};

var mapDispatchToProps = {
  openSlateInPane: openSlateInPane,
};

export default connect(mapStateToProps, mapDispatchToProps)(CustomModuleContainer);
